/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import io_conceptImg from 'assets/images/io-concept.gif'
import bounding_boxesImg from 'assets/images/bounding-boxes.gif'
import root_marginImg from 'assets/images/root-margin.gif'
import intersect_ratioImg from 'assets/images/intersect-ratio.jpeg'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - IntersectionObserver API</h2>
    <p className={styles.description}>
      Современные веб-страницы активно используют событие скрола для обнаружения элементов, видимых в границах текущей
      области просмотра (viewport). Скролинг может запускать отложенную загрузку изображений и данных, анимацию
      элементов, поддерживать бесконечную загрузку содержимого и многое другое. К сожалению, событие прокрутки и рассчет
      геометрии элементов (вызовы <code className={styles.code}>Element.getBoundingClientRect()</code>) очень
      ресурсоемкие операции выполняющиеся в основном потоке . Это вызывает проблемы в реализации и часто приводит к
      снижению производительности браузера.{' '}
    </p>
    <p className={styles.description}>
      <code className={styles.code}>Intersection Observer API</code> был создан как решение этих проблем и новый способ
      обработки событий прокрутки. Этот интерфейс предоставляет возможность наблюдать за заданными элементами и упрощает
      реализацию отслеживания изменения в их пересечении с заданным элементом-предком или самим окном просмотра
      (viewport), то есть слежения за видимостью элемента.
    </p>
    <p className={styles.description}>
      <code className={styles.code}>Intersection Observer</code> передает управление событиями пересечения браузеру,
      который управляет этими событиями более эффективно, улучшая производительность.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Концепция Intersection Observer</h3>
      <p className={styles.description}>
        Рассмотрим несколько ключевых терминов используютщихся для определения любого экземпляра Intersection Observer.{' '}
      </p>
      <p className={styles.description}>
        <b>Observer (наблюдатель, обзервер)</b> - результат вызова класса{' '}
        <code className={styles.code}>IntersectionObserver</code>, его экземпляр, объект с методами.{' '}
      </p>
      <p className={styles.description}>
        <b>Root (корень, контекст)</b> - это элемент, который ожидает пересечения элемента-цели. Основа наблюдателя. По
        умолчанию это окно просмотра браузера (viewport), но может использоваться любой другой элемент, если этого
        требует задача.{' '}
      </p>
      <p className={styles.description}>
        <b>Target (цель)</b> - элемент, за которым следит наблюдатель и оповещает о его вхождении в корень. Целью может
        быть любой элемент. Один наблюдатель может отслеживать множество различных целей.{' '}
      </p>
      <img className={styles.img} src={io_conceptImg} alt="io-concept" />
      <p className={styles.description}>
        При рассчете пересечений, Intesection Observer рассматривает все элементы как прямоугольник, вне зависимости от
        их внешнего вида. Эти прямоугольники рассчитываются как наименьшие из возможных, при этом они содержат все
        целевое содержимое.{' '}
      </p>
      <img className={styles.img} src={bounding_boxesImg} alt="bounding-boxes" />
      <p className={styles.description}>
        Можно увеличить или уменьшить размеры границ корня для рассчета пересечений. Для этого используется{' '}
        <code className={styles.code}>rootMargin</code>. При этом геометрия корня не меняется, изменения касаются только
        рассчетов границ пересечения.{' '}
      </p>
      <img className={styles.img} src={root_marginImg} alt="root-margin" />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Использование Intersection Observer</h3>
      <p className={styles.description}>
        Достаточно создать новый экземпляр класса <code className={styles.code}>IntersectionObserver</code>, принимающий
        два аргумента — callback-функцию и объект настроек.{' '}
      </p>
      <CopyBlock text={`IntersectionObserver(callback, options)`} language="js" showLineNumbers={false} theme={ocean} />
      <p className={styles.description}>
        По умолчанию, коллбэк будет вызываться, когда элемент появляется и исчезает из области видимости. Первым
        аргументом коллбэк получит массив объектов <code className={styles.code}>IntersectionObserverEntry</code>,
        каждый из которых содержит набор свойств элемента, показываемого в области видимости:{' '}
        <code className={styles.code}>boundingClientRect</code>, <code className={styles.code}>intersectionRatio</code>,{' '}
        <code className={styles.code}>intersectionRect</code>, <code className={styles.code}>rootBounds</code>,{' '}
        <code className={styles.code}>target</code> и <code className={styles.code}>time</code>. Вторым агументом в
        колбек передается ссылка на сам экземпляр обзервера.{' '}
      </p>
      <p className={styles.description}>
        Объект настроек позволяет задать опции, с которыми будет создан обзервер. Можно изменить контекст (root),
        который по умолчанию равен <code className={styles.code}>viewport</code>, задать отступы от границ контекста
        (rootMargin) — по умолчанию <code className={styles.code}>0px</code>. И порог вхождения (threshold) в виде
        массива значений. Порог означает, сколько процентов элемента должно попасть в область видимости для срабатывания
        коллбэка. Значения могут варьироваться от <code className={styles.code}>0.01</code> до{' '}
        <code className={styles.code}>1.0</code>.{' '}
      </p>
      <img className={styles.img} src={intersect_ratioImg} alt="intersect_ratioImg" />
      <p className={styles.description}>
        Экземпляр класса это наблюдатель, объект с набором методов, с помощью которых можно следить за DOM-элементами.{' '}
      </p>
      <CopyBlock
        text={`const options = {
  rootMargin: '50px',
  threshold: 0.5,
};

const onEntry = (entries, observer) => {
  entries.forEach(entry => {
    // тут можно писать логику для проверки вхождения
  });
};

const observer = new IntersectionObserver(onEntry, options);`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        После того как экземпляр <code className={styles.code}>IntersectionObserver</code> создан, необходимо
        предоставить один или несколько целевых элементов для наблюдения. Для этого на экземляре вызывается метод{' '}
        <code className={styles.code}>observe(elem)</code>, который принимает ссылку на DOM-элемент за которым
        необходимо наблюдать. Когда элемент войдет (или выйдет) в область видимости (root), будет вызвана
        callback-функция переданная при создании экземпляра.{' '}
      </p>
      <CopyBlock
        text={`const options = {
  rootMargin: '50px',
  threshold: 0.5,
};

const onEntry = (entries, observer) => {
  entries.forEach(entry => {
    // тут можно писать логику для проверки вхождения
  });
};

const observer = new IntersectionObserver(onEntry, options);

observer.observe(elem);`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Полифил Intersection Observer</h3>
      <p className={styles.description}>
        Хотя <code className={styles.code}>Intersection Observer API</code> поддерживается всеми последними версиями
        современных браузеров, следует позаботиться о полифиле, чтобы он гарантированно работал во всех актуальных
        версиях. Полифил от WICG можно взять из{' '}
        <a
          className={styles.link}
          href="https://github.com/w3c/IntersectionObserver/tree/main/polyfill"
          target="_blank"
          rel="noopener noreferrer"
        >
          этого GitHub-репозитория
        </a>
        .
      </p>
      <p className={styles.description}>
        Самый простой способ добавить полифил - это использовать{' '}
        <a className={styles.link} rel="noopener noreferrer" href="https://polyfill.io/v3/" target="_blank">
          polyfill.io
        </a>
        . Он загружает только указанные полифилы, и только тогда, когда браузер требует этого. Это помогает свести вес
        страницы к минимуму, но позволяет полифиллу работать без дальнейшей настройки.
      </p>
      <p className={styles.notificationWarn}>
        Единственным недостатком полифилла является то, что вы не получите преимуществ в производительности в старых
        браузерах, но скрипт будет работать и пользователь сможет взаимодействовать с веб-страницей.
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.4 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://www.smashingmagazine.com/2018/01/deferring-lazy-loading-intersection-observer-api/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Now You See Me: How To Defer, Lazy-Load And Act With IntersectionObserver
          </a>
        </li>
      </ul>
    </section>
  </section>
))
